import config from '@config';
import { getLanguage } from '@shared/i18n';

import { getAppId } from './app-id';
import { SCHEMA_VERSION, type Envelope } from './envelope';
import { identityOf } from './identity';
import { sessionIdFor } from './session';
import { uuid } from './uuid';

/**
 * Whether the app considers itself offline. `navigator.onLine` alone says
 * "online" on a network that reaches nothing; the app's own answer comes
 * from a reachability probe (`useIsOnline`), which `app.tsx` passes in here
 * so an event's `offline` flag agrees with what the reader was shown.
 */
let offline: boolean | undefined;
const connectivityListeners = new Set<() => void>();

export function setOffline(isOffline: boolean): void {
	const changed = offline !== isOffline;
	offline = isOffline;
	if (changed) {
		connectivityListeners.forEach((listener) => listener());
	}
}

export function isOffline(): boolean {
	return offline ?? !navigator.onLine;
}

/** Called when the app goes offline or comes back, for whoever was waiting to send. */
export function onConnectivityChange(listener: () => void): () => void {
	connectivityListeners.add(listener);
	return () => {
		connectivityListeners.delete(listener);
	};
}

function getDisplayMode(): Envelope['display_mode'] {
	const standalone =
		window.matchMedia?.('(display-mode: standalone)').matches ||
		(navigator as { standalone?: boolean }).standalone === true;
	return standalone ? 'installed' : 'browser';
}

/** The fields `track()` stamps on every event. */
export function buildEnvelope(): Envelope {
	const appId = getAppId();
	const now = Date.now();
	return {
		// Left out, not `undefined`, until the device has it: the row is sent
		// and stored as it is.
		...(appId === undefined ? {} : { app_id: appId }),
		event_id: uuid(),
		schema_version: SCHEMA_VERSION,
		event_ts: now,
		session_id: sessionIdFor(now),
		...identityOf(),
		build_version: config.build.version,
		display_mode: getDisplayMode(),
		language: getLanguage(),
		offline: isOffline(),
	};
}
